import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { userService, chatService, notificationService, ChatConversation } from '../services';
import { Notification } from '../interfaces/Notification';
import { notifyCreditsUpdated } from '../utils/creditsEvents';

interface GlobalDataContextProps {
  // Credits
  credits: number;
  isLoadingCredits: boolean;
  refreshCredits: (forceRefresh?: boolean) => Promise<void>;
  updateCredits: (newCredits: number) => void;
  notifyCreditsUpdated: () => void;

  // Notifications
  notifications: Notification[];
  unreadCount: number;
  isLoadingNotifications: boolean;
  refreshNotifications: () => Promise<void>;
  markNotificationAsRead: (notificationId: number) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;

  // Chat conversations
  conversations: ChatConversation[];
  isLoadingConversations: boolean;
  refreshConversations: (forceRefresh?: boolean) => Promise<void>;

  // Global loading state
  isGlobalLoading: boolean;

  // Error handling
  globalError: string | null;
  clearGlobalError: () => void;
}

const GlobalDataContext = createContext<GlobalDataContextProps | undefined>(undefined);

export const GlobalDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Credits state
  const [credits, setCredits] = useState<number>(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState<boolean>(false);

  // Notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState<boolean>(false);

  // Chat conversations state
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState<boolean>(false);

  // Global states
  const [isGlobalLoading] = useState<boolean>(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Funzione per caricare i crediti
  const refreshCredits = useCallback(async (forceRefresh = false) => {
    setIsLoadingCredits(true);
    try {
      const data = await userService.getCredits(forceRefresh);
      setCredits(data.credits);
    } catch (error) {
      console.error('Error fetching credits:', error);
      setGlobalError('Errore nel caricamento dei crediti');
    } finally {
      setIsLoadingCredits(false);
    }
  }, []);

  // Funzione per aggiornare i crediti manualmente
  const updateCredits = useCallback((newCredits: number) => {
    setCredits(newCredits);
    // Invalida la cache dei crediti
    userService.invalidateCreditsCache();
  }, []);

  // Funzione helper per notificare l'aggiornamento dei crediti
  const notifyCreditsUpdatedContext = useCallback(() => {
    notifyCreditsUpdated();
  }, []);

  // Funzione per caricare le notifiche
  const refreshNotifications = useCallback(async () => {
    const authToken = sessionStorage.getItem('authToken');
    if (!authToken) return;

    setIsLoadingNotifications(true);
    try {
      const notificationsList = await notificationService.getNotifications();

      setNotifications(notificationsList);
      const unread = notificationsList.filter((n) => n.read === null).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setGlobalError('Errore nel caricamento delle notifiche');
    } finally {
      setIsLoadingNotifications(false);
    }
  }, []);

  // Funzione per marcare una notifica come letta
  const markNotificationAsRead = useCallback(async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);

      // Aggiorna lo stato locale
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, read: new Date().toISOString() } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Funzione per marcare tutte le notifiche come lette
  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();

      // Aggiorna lo stato locale
      setNotifications(prev => prev.map(n => ({ ...n, read: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Funzione per caricare le conversazioni chat
  const refreshConversations = useCallback(async (forceRefresh = false) => {
    const authToken = sessionStorage.getItem('authToken');
    if (!authToken) return;

    setIsLoadingConversations(true);
    try {
      const data = await chatService.getConversations(forceRefresh);
      setConversations(data.conversations || []);
    } catch (error: any) {
      console.error('Error fetching conversations:', error);

      // Gestione specifica per errori 500 (problemi backend)
      if (error.response?.status === 500) {
        console.error('🚨 Server error 500 in GlobalDataContext - Backend problem detected');
        // Non mostrare toast per errori 500, solo log
      } else if (error.response?.status === 401) {
        console.error('🚨 Unauthorized in GlobalDataContext - Token expired or invalid');
        // Non mostrare toast per errori 401, solo log
      }

      // Non mostrare errori globali per le conversazioni chat
      // Gli errori vengono gestiti dai componenti specifici
      setConversations([]); // Imposta array vuoto in caso di errore
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Clear global error
  const clearGlobalError = useCallback(() => {
    setGlobalError(null);
  }, []);

  // Carica i dati iniziali quando l'utente è autenticato
  useEffect(() => {
    const authToken = sessionStorage.getItem('authToken');
    if (authToken) {
      // Carica solo crediti e notifiche globalmente
      // Le conversazioni chat vengono gestite dai componenti specifici
      Promise.all([
        refreshCredits(),
        refreshNotifications()
      ]).catch(console.error);
    }
  }, [refreshCredits, refreshNotifications]); // Solo al mount

  // Listener per rilevare quando l'utente si autentica o disautentica
  useEffect(() => {
    const checkAuthAndReload = () => {
      const authToken = sessionStorage.getItem('authToken');
      if (authToken) {
        // Ricarica i crediti quando l'utente si autentica
        refreshCredits(true).catch(console.error);
        refreshNotifications().catch(console.error);
      } else {
        // Reset dei dati quando l'utente si disautentica
        setCredits(0);
        setNotifications([]);
        setUnreadCount(0);
        setConversations([]);
      }
    };

    // Controlla quando la pagina diventa visibile (utile per tab multipli e focus)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAuthAndReload();
      }
    };

    // Listener per cambiamenti nello storage (utile per tab multipli)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        checkAuthAndReload();
      }
    };

    // Listener per eventi personalizzati di autenticazione
    const handleAuthChange = () => {
      checkAuthAndReload();
    };

    // Listener per forzare il caricamento dei crediti
    const handleForceLoadCredits = () => {
      const authToken = sessionStorage.getItem('authToken');
      if (authToken) {
        refreshCredits(true).catch(console.error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStateChanged', handleAuthChange);
    window.addEventListener('forceLoadCredits', handleForceLoadCredits);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthChange);
      window.removeEventListener('forceLoadCredits', handleForceLoadCredits);
    };
  }, [refreshCredits, refreshNotifications]);


  // Memorizza il valore del context
  const value = useMemo(
    () => ({
      // Credits
      credits,
      isLoadingCredits,
      refreshCredits,
      updateCredits,
      notifyCreditsUpdated: notifyCreditsUpdatedContext,

      // Notifications
      notifications,
      unreadCount,
      isLoadingNotifications,
      refreshNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,

      // Chat conversations
      conversations,
      isLoadingConversations,
      refreshConversations,

      // Global states
      isGlobalLoading,
      globalError,
      clearGlobalError,
    }),
    [
      credits,
      isLoadingCredits,
      refreshCredits,
      updateCredits,
      notifyCreditsUpdatedContext,
      notifications,
      unreadCount,
      isLoadingNotifications,
      refreshNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      conversations,
      isLoadingConversations,
      refreshConversations,
      isGlobalLoading,
      globalError,
      clearGlobalError,
    ]
  );

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
};

// Hook personalizzato per usare il context
export const useGlobalData = () => {
  const context = useContext(GlobalDataContext);
  if (!context) {
    throw new Error('useGlobalData must be used within a GlobalDataProvider');
  }
  return context;
};

// Export del context per casi speciali
export { GlobalDataContext };
